import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { resetPassword } from "@/api/endpoints";
import { usePageTitle } from "@/hooks/usePageTitle";

const MIN_LENGTH = 12;

export function ResetPassword(): JSX.Element {
  usePageTitle("Choose a new password");
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState(false);

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < MIN_LENGTH;

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (password !== confirm || password.length < MIN_LENGTH) return;
    setPending(true);
    setError(null);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 400
          ? "This reset link is invalid or has expired. Request a new one below."
          : "Something went wrong — please try again.",
      );
    } finally {
      setPending(false);
    }
  }

  if (!token) {
    return (
      <main className="min-h-full flex items-center justify-center px-4">
        <div className="card w-full max-w-sm space-y-4">
          <h1 className="text-xl font-semibold">Invalid link</h1>
          <p className="text-sm text-ink-300">
            This page needs the reset link from your email. If yours has
            expired, request a new one.
          </p>
          <Link to="/forgot-password" className="btn-primary w-full">
            Request a new link
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-full flex items-center justify-center px-4">
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="card w-full max-w-sm space-y-4"
        noValidate
        aria-describedby={error ? "reset-error" : undefined}
      >
        <header className="space-y-1">
          <h1 className="text-xl font-semibold">Choose a new password</h1>
          <p className="text-sm text-ink-300">
            At least {MIN_LENGTH} characters. Signing in on other devices will
            require the new password.
          </p>
        </header>

        {done ? (
          <div className="space-y-4">
            <p className="text-sm text-ink-200" role="status">
              Your password has been reset.
            </p>
            <Link to="/login" className="btn-primary w-full">
              Sign in
            </Link>
          </div>
        ) : (
          <>
            <label className="block space-y-1">
              <span className="text-sm text-ink-200">New password</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_LENGTH}
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {tooShort && (
                <span className="text-xs text-severity-medium">
                  {MIN_LENGTH - password.length} more character
                  {MIN_LENGTH - password.length === 1 ? "" : "s"} needed
                </span>
              )}
            </label>

            <label className="block space-y-1">
              <span className="text-sm text-ink-200">Confirm new password</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                className="input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              {mismatch && (
                <span className="text-xs text-severity-medium">Passwords don&apos;t match</span>
              )}
            </label>

            {error && (
              <div className="space-y-2" id="reset-error" role="alert">
                <p className="text-sm text-severity-high">{error}</p>
                <p className="text-sm text-ink-400">
                  <Link to="/forgot-password" className="hover:text-ink-100 underline">
                    Request a new link
                  </Link>
                </p>
              </div>
            )}

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={pending || mismatch || tooShort || !password}
            >
              {pending ? "Resetting…" : "Reset password"}
            </button>
          </>
        )}
      </form>
    </main>
  );
}
